import { createFileRoute } from '@tanstack/react-router'
import { Home } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { FeatureDetail } from '@/features/features/components/FeatureDetail'
import { useFeatureQuery } from '@/features/features/hooks/useFeatures'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/shared/components/ui/breadcrumb'
import { useBreadcrumbs } from '@/shared/hooks/useBreadcrumbs'

export const Route = createFileRoute('/_auth/features/$featureId')({
  component: FeatureDetailRoute,
})

function FeatureDetailRoute() {
  const { featureId } = Route.useParams()
  const { setBreadcrumbs } = useBreadcrumbs()

  const { data: feature, isLoading, error } = useFeatureQuery(featureId)

  const breadcrumbsContent = useMemo(() => {
    if (!feature) return null

    return (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink to='/dashboard' className='flex items-center gap-1'>
              <Home className='w-4 h-4' />
              Dashboard
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink to='/features'>Non-Agentic Features</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{feature.feature_name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    )
  }, [feature])

  useEffect(() => {
    if (breadcrumbsContent) {
      setBreadcrumbs(breadcrumbsContent)
    }
  }, [setBreadcrumbs, breadcrumbsContent])

  if (isLoading) {
    return (
      <div className='container mx-auto py-6'>
        <div className='flex items-center justify-center min-h-[200px]'>
          <div className='text-center'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4'></div>
            <p className='text-muted-foreground'>Loading feature details...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !feature) {
    return (
      <div className='container mx-auto py-6'>
        <div className='text-center min-h-[200px] flex items-center justify-center'>
          <div>
            <p className='text-muted-foreground mb-4'>
              {error ? 'Failed to load feature details' : 'Feature not found'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return <FeatureDetail feature={feature} featureId={featureId} />
}
